import { Form, Loading, SubmitButton, TextAreaInputField } from '@/components';
import {
  useAccountTitles,
  useAccountingTypeOptions,
} from '@/features/master-table/hooks';
import { useRequiredByEndYearOrOutcomeTargetYear } from '@/features/project/_hooks/form-control';
import { useProject } from '@/features/project/_hooks/project';
import { InspectionEvaluationFormSchema } from '@/features/project/projects/_models/project';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import type { sheetTypeEnum } from '@/utils/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof InspectionEvaluationFormSchema>;
type InitialFormValues = Partial<FormValues>;

type Props = {
  fiscalYear: number;
  type: z.infer<typeof sheetTypeEnum>;
  initialValues?: InitialFormValues;
  onSubmit: (values: FormValues) => void;
};

export const InspectionEvaluationForm: FC<Props> = ({
  fiscalYear: _fiscalYear,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit: _unit } = useLocaledAmount();
  const { id } = useId();
  const _project = useProject(id);
  const _accountingTypeOptions = useAccountingTypeOptions({ type: 'special' });
  const _accountTitles = useAccountTitles();

  const { isLoading } = useRequiredByEndYearOrOutcomeTargetYear(id);

  const method = useForm<InitialFormValues, FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(InspectionEvaluationFormSchema),
  });

  const {
    handleSubmit,
    watch,
    setValue: _setValue,
    resetField: _resetField,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = method;

  const {
    fields: _summariesAndResponsesFields,
    append: _appendSummariesAndResponses,
    remove: _removeSummariesAndResponses,
  } = useFieldArray({
    control,
    name: 'summariesAndResponses',
  });

  const {
    fields: _indicatedItemsAndResponsesFields,
    append: _appendIndicatedItemsAndResponses,
    remove: _removeIndicatedItemsAndResponses,
  } = useFieldArray({
    control,
    name: 'otherIndicatedItemsAndResponses',
  });

  const {
    fields: _specialAccountFields,
    append: _appendSpecialAccount,
    remove: _removeSpecialAccount,
  } = useFieldArray({
    control,
    name: 'reflectionStatusBasedOnOpinion.specialAccount',
  });

  const [
    reflectionStatusBasedOnOpinionAction,
    _inspectionByExternalExpertReviewType,
    _inspectionByExternalExpertOpinion,
    _inspectionByExternalExpertResultOfPublishing,
  ] = watch([
    'reflectionStatusBasedOnOpinion.action',
    'inspectionByExternalExpert.reviewType',
    'inspectionByExternalExpert.opinion',
    'inspectionByExternalExpert.resultOfPublishing',
  ]);

  const _disabledReflectionStatusBasedOnOpinionAmount = useMemo(
    () =>
      reflectionStatusBasedOnOpinionAction !== 'abolition' &&
      reflectionStatusBasedOnOpinionAction !== 'reduction',
    [reflectionStatusBasedOnOpinionAction],
  );

  return isLoading ? (
    <Loading />
  ) : (
    <FormProvider {...method}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit((values) => {
          // if (
          //   requiredEvaluation &&
          //   type !== 'KS' &&
          //   !values.inspectionAndImprovementByAdministrativeJurisdiction
          //     .evaluation
          // ) {
          //   setError(
          //     'inspectionAndImprovementByAdministrativeJurisdiction.evaluation',
          //     {
          //       type: 'required',
          //       message: t('validation.required'),
          //     },
          //   );
          //   return;
          // }

          onSubmit(values);
        })}
      >
        <TextAreaInputField
          name="inspectionAndImprovementByAdministrativeJurisdiction.analysisOfValidity"
          label={t('penjelasan-tentang-kegiatan-penerima-dana')}
          control={control}
          errors={errors}
          rows={10}
        />
        {/* {type === 'RS' && (
          <>
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'inspection-and-improvement-by-administrative-jurisdiction',
                )}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.analysisOfValidity"
              label="20.予算活用の妥当性の分析"
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              hint={t('hint-inspection-result-by-administrative-jurisdiction')}
              control={control}
              errors={errors}
              rows={2}
              required
            />
            <EvaluationField required={requiredEvaluation} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              hint={t(
                'hint-direction-of-improvement-by-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              rows={2}
              required
            />
          </>
        )} */}
        {/* 
        {type === 'SS' && (
          <>
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'inspection-and-improvement-by-iaa-administrative-jurisdiction',
                )}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.analysisOfValidity"
              label="20.予算活用の妥当性の分析"
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              hint={t(
                'hint-inspection-result-by-iaa-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              required
            />
            <EvaluationField required={requiredEvaluation} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              hint={t(
                'hint-direction-of-improvement-by-iaa-administrative-jurisdiction',
              )}
              control={control}
              errors={errors}
              required
            />
          </>
        )}

        {type === 'KS' && (
          <>
            {isBefore2025(project.data) && (
              <>
                <Box marginBottom={12}>
                  <Text variant="h5" text={t('appropriateness-inspection')} />
                </Box>
                <TextAreaInputField
                  name="appropriatenessInspection.reasonsWhyBulkDeliveringWasNecessary"
                  label={t('reason-why-bulk-delivering-was-necessary')}
                  hint={t('hint-reason-why-bulk-delivering-was-necessary')}
                  control={control}
                  errors={errors}
                  rows={2}
                />
                <TextAreaInputField
                  name="appropriatenessInspection.approachInDeterminingTheTimingAndAmountOfAdditions"
                  label={t(
                    'approach-in-determining-the-timing-and-amount-of-additions',
                  )}
                  hint={t(
                    'hint-approach-in-determining-the-timing-and-amount-of-additions',
                  )}
                  control={control}
                  errors={errors}
                  rows={2}
                />
              </>
            )}
            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('inspection-and-improvement-by-fund-jurisdiction')}
              />
            </Box>
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.analysisOfValidity"
              label="20.予算活用の妥当性の分析"
              control={control}
              errors={errors}
              rows={2}
            />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.inspectionResult"
              label={t('inspection-result')}
              control={control}
              errors={errors}
              rows={2}
            />
            <EvaluationField required={false} type={type} />
            <TextAreaInputField
              name="inspectionAndImprovementByAdministrativeJurisdiction.directionOfImprovement"
              label={t('direction-of-improvement')}
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text variant="h5" text={t('findings-of-external-experts')} />
            </Box>
            <TextAreaInputField
              name="findingsOfExternalExperts"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t(
                  'process-leading-to-the-findings-of-review-promotion-team',
                )}
              />
            </Box>
            <TextAreaInputField
              name="processLeadingToTheFindingsOfReviewPromotionTeam"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            <Box marginBottom={12}>
              <Text
                variant="h5"
                text={t('improvement-point-based-on-the-findings')}
              />
            </Box>
            <TextAreaInputField
              name="improvementPointBasedOnTheFindings"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />

            {isBefore2025(project.data) && (
              <>
                <Box marginBottom={12}>
                  <Text
                    variant="h5"
                    text={t('overview-of-reviews-carried-out-in-the-past')}
                  />
                </Box>
                <TextAreaInputField
                  name="overviewOfReviewsCarriedOutInThePast"
                  label=""
                  control={control}
                  errors={errors}
                  rows={2}
                />
              </>
            )}
          </>
        )} */}

        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      </Form>
    </FormProvider>
  );
};

// const EvaluationField: FC<{
//   required: boolean;
//   type: z.infer<typeof sheetTypeEnum>;
// }> = ({ required, type }) => {
//   const { t } = useTranslation();

//   const {
//     control,
//     formState: { errors },
//   } = useFormContext<FormValues>();

//   const hint =
//     type === 'RS'
//       ? t('hint-evaluation-by-administrative-jurisdiction')
//       : type === 'SS'
//         ? t('hint-evaluation-by-iaa-administrative-jurisdiction')
//         : undefined;

//   return (
//     <TextAreaInputField
//       name="inspectionAndImprovementByAdministrativeJurisdiction.evaluation"
//       label={t('evaluation')}
//       hint={hint}
//       control={control}
//       errors={errors}
//       required={required}
//       rows={2}
//     />
//   );
// };
